import { useMemo, useRef, HTMLAttributes, SVGTextElementAttributes } from 'react'
import { useMediaQuery } from 'react-responsive'
import { line, arc } from 'd3'
import cn from 'classnames'
import { useResizeObserver } from '~/hooks/client/useResizeObserver'

interface ArcSmallProps extends HTMLAttributes<HTMLDivElement>
{
    min?: number
    max?: number
    value: number
    fontFamily?: string
}

interface ChartConfig
{
    transformX: number
    transformY: number
    serifSize: number
}

interface TextConfig
{
    dx: number
    dy: number
    fontSize: number
    letterSpacing: number
    textAnchor: SVGTextElementAttributes<SVGTextElement>['textAnchor']
}

interface Serif
{
    x1: number
    y1: number
    x2: number
    y2: number
}

export function ArcSmall ({
    min = 0,
    max = 100,
    value,
    fontFamily = 'monospace',
    className = '',
}: ArcSmallProps)
{
    const isMobile = useMediaQuery({ query: '(max-width: 767px)' })
    const textRef = useRef<SVGTextElement>(null)
    const rootRef = useRef<HTMLDivElement>(null)

    const svgSize = useResizeObserver(rootRef)

    const textSize = useResizeObserver(textRef)

    const chartConfig = useMemo<ChartConfig>(() => (
        {
            transformX: isMobile
                ? textSize.width + 12
                : svgSize.width / 2,
            transformY: isMobile
                ? svgSize.height / 2
                : svgSize.height - 1,
            serifSize: isMobile
                ? 8
                : 4,
        }
    ), [isMobile, textSize.width, svgSize.width, svgSize.height])

    const textConfig = useMemo<TextConfig>(() => (
        {
            dx: isMobile
                ? 0
                : svgSize.width / 2,
            dy: isMobile
                ? svgSize.height / 2
                : svgSize.height - 10,
            fontSize: 16,
            letterSpacing: 0.66,
            textAnchor: isMobile
                ? 'start'
                : 'middle',
        }
    ), [isMobile, svgSize.width, svgSize.height])

    const percentage = useMemo(() =>
    {
        const range = max - min
        if (range === 0) {
            return 0
        }

        const adjusted = min >= 0
            ? value - min
            : value + min
        return Math.round(adjusted / range * 100 * 100) / 100
    }, [value, min, max])

    const {
        paths,
        serifs,
    } = useMemo(() =>
    {
        if (isMobile) {
            const l = line<number[]>().x(d => d[0]).y(d => d[1])
            const start = 0
            const end = svgSize.width - chartConfig.transformX - 1
            const bg = l([[start, 0], [end, 0]]) ?? ''
            const fgW = percentage / 100 * (
                end - start
            )
            const fg = l([[start, 0], [start + fgW, 0]]) ?? ''
            const half = svgSize.height / 2
            const s: { start: Serif; end: Serif } = {
                start: {
                    x1: chartConfig.transformX,
                    y1: half - chartConfig.serifSize / 2,
                    x2: chartConfig.transformX,
                    y2: half + chartConfig.serifSize / 2,
                },
                end: {
                    x1: svgSize.width - 1,
                    y1: half - chartConfig.serifSize / 2,
                    x2: svgSize.width - 1,
                    y2: half + chartConfig.serifSize / 2,
                },
            }
            return {
                paths: {
                    bg,
                    fg,
                },
                serifs: s,
            }
        }

        /* desktop (semi-circle) */
        const radius = (
                       svgSize.width - 2
                       ) / 2 - 2
        const arcGen = arc()
        const bg = arcGen({
            outerRadius: radius,
            innerRadius: radius,
            startAngle: -Math.PI / 2,
            endAngle: Math.PI / 2,
        }) ?? ''
        const fg = arcGen({
            outerRadius: radius,
            innerRadius: radius,
            startAngle: -Math.PI / 2,
            endAngle: (
                          -Math.PI / 2
                      ) + (
                          percentage / 100
                      ) * Math.PI,
        }) ?? ''
        const s: { start: Serif; end: Serif } = {
            start: {
                x1: 1,
                y1: svgSize.height - 1,
                x2: chartConfig.serifSize + 1,
                y2: svgSize.height - 1,
            },
            end: {
                x1: svgSize.width - 1,
                y1: svgSize.height - 1,
                x2: svgSize.width - chartConfig.serifSize - 1,
                y2: svgSize.height - 1,
            },
        }
        return {
            paths: {
                bg,
                fg,
            },
            serifs: s,
        }
    }, [isMobile, chartConfig, percentage, textSize.width, svgSize.width, svgSize.height])

    /* ───────────────────────────────── render ──────────────────────────────── */
    let percentageStr = `${percentage}`

    if (!percentageStr.includes('.')) {
        if (percentage !== 100 && percentage !== 0) {
            percentageStr += '.00'
        }
    }  else {
        percentageStr = percentageStr.padEnd(2, '0')
    }

    let text = `${percentageStr}%`;
    if (isNaN(percentage)) {
        text = `?%`
    }

    return (
        <div className={cn('relative', className)} ref={rootRef}>
            <svg viewBox={`0 0 ${svgSize.width} ${svgSize.height}`} className={'w-full h-full'}>
                {/* serifs */}
                <line {...serifs.start} strokeWidth={1}
                      strokeOpacity={value > 0
                          ? 1
                          : 0.3}
                      className={'stroke-accent'}
                />
                <line {...serifs.end}
                      strokeWidth={1}
                      strokeOpacity={value >= max
                          ? 1
                          : 0.3}
                      className={'stroke-accent'}
                />

                {/* paths */}
                <path
                    d={paths.bg}
                    strokeWidth={1}
                    strokeOpacity={0.3}
                    fill="none"
                    transform={`translate(${chartConfig.transformX}, ${chartConfig.transformY})`}
                    className={'stroke-accent'}
                />
                <path
                    d={paths.fg}
                    strokeWidth={1}
                    strokeOpacity={0.8}
                    fill="none"
                    transform={`translate(${chartConfig.transformX}, ${chartConfig.transformY})`}
                    className={'stroke-accent'}
                />

                {/* percentage label */}
                <text
                    ref={textRef}
                    textAnchor={textConfig.textAnchor}
                    alignmentBaseline="middle"
                    dx={textConfig.dx}
                    dy={textConfig.dy}
                    fontSize={textConfig.fontSize}
                    fontFamily={fontFamily}
                    fontWeight={500}
                    letterSpacing={textConfig.letterSpacing}
                    className={'fill-accent'}
                >
                    {text}
                </text>
            </svg>
        </div>
    )
}